<template>
  <div class="reqtrack">
    <div v-if="!embedded" class="reqtrack__head">
      <h4 class="reqtrack__title">{{ zk('进度管理', '구매내역관리') }}</h4>
      <p class="reqtrack__sub">{{ zk('按求购商品显示各阶段进度 · 停留天数 · 突出长期未处理 (横向滚动 →)', '요청상품별 단계 진행 · 체류기간 · 방치 강조 (가로 스크롤 →)') }}</p>
    </div>

    <!-- 현황 대시보드: 단계별 / 체류기간별 2행 -->
    <div class="reqtrack__dash">
      <div class="reqtrack__dash-row">
        <span class="reqtrack__dash-title">{{ zk('按阶段', '단계별') }}</span>
        <button
          v-for="chip in stageChips"
          :key="chip.key"
          type="button"
          class="reqtrack__card"
          :class="{
            'reqtrack__card--active': activeFilter === chip.key,
            'reqtrack__card--all': chip.key === 'all',
            'reqtrack__card--empty': chip.count === 0,
            'reqtrack__card--warn': chip.warn > 0
          }"
          @click="setFilter(chip.key)">
          <span class="reqtrack__card-label">{{ chip.label }}</span>
          <span class="reqtrack__card-row">
            <span class="reqtrack__card-count">{{ chip.count }}</span>
            <span v-if="chip.warn" class="reqtrack__card-warn">⚠{{ chip.warn }}</span>
          </span>
        </button>
      </div>
      <div class="reqtrack__dash-row">
        <span class="reqtrack__dash-title">{{ zk('停留天数', '체류기간') }}</span>
        <button
          v-for="b in agingBuckets"
          :key="b.key"
          type="button"
          class="reqtrack__card reqtrack__card--aging"
          :class="[`reqtrack__card--${b.lv}`, { 'reqtrack__card--active': activeFilter === b.key, 'reqtrack__card--empty': b.count === 0 }]"
          @click="setFilter(b.key)">
          <span class="reqtrack__card-label">{{ b.label }}</span>
          <span class="reqtrack__card-count">{{ b.count }}</span>
        </button>
      </div>
    </div>

    <div v-if="rows.length && !loading" class="reqtrack__toolbar">
      <label class="reqtrack__switch">
        <input type="checkbox" v-model="memoPreview">
        <span class="reqtrack__switch-track"><span class="reqtrack__switch-thumb"></span></span>
        <span class="reqtrack__switch-label">{{ zk('备注预览', '메모 미리보기') }}</span>
      </label>
    </div>

    <div v-if="loading" class="reqtrack__state">{{ zk('加载中...', '불러오는 중...') }}</div>
    <div v-else-if="!rows.length" class="reqtrack__state">{{ zk('没有可显示的求购', '표시할 구매요청이 없습니다.') }}</div>

    <CDataTable
      v-else
      storage-key="reqpurchaseTracking"
      :headers="tableHeaders"
      :items="filteredRows"
      item-key="_id"
      :fixedColumns="'상품정보'"
      :isRadio="false"
      height="640px">

      <!-- 좌측 고정: 상품정보 -->
      <template v-slot:상품정보="{ item }">
        <div class="reqtrack__prod">
          <img
            v-if="item.p.thumb"
            :src="item.p.thumb"
            class="reqtrack__thumb"
            alt="상품 이미지"
            @error="onImgError">
          <span v-else class="reqtrack__thumb reqtrack__thumb--empty"></span>
          <div class="reqtrack__prod-info">
            <div class="reqtrack__pname">{{ item.p.name }}</div>
            <div class="reqtrack__popt">{{ item.p.opt }}</div>
            <div class="reqtrack__pmeta">{{ item.p.meta }}</div>
            <a v-if="item.photos && item.photos.length" href="javascript:void(0)" class="reqtrack__photo-link" @click.stop="photoView = item">📷 {{ zk('入库照片', '입고사진') }} {{ item.photos.length }}</a>
          </div>
        </div>
      </template>

      <!-- 단계 셀들: 동적 슬롯 -->
      <template
        v-for="stage in stages"
        v-slot:[stage.k]="{ item }">
        <div
          :key="stage.k"
          class="reqtrack__cellwrap"
          :class="[cellAttrs(item, stage).class, { 'reqtrack__cellwrap--clickable': cellState(item, stage) !== 'future' && cellState(item, stage) !== 'skip' }]"
          @click="onCellClick(stage, item)">
          <!-- 선적이 있으면 선적별 미니줄로 그린다(정본 §4 Phase 2-17).
               ①②가 칸을 가로질러 정렬돼서 어느 선적이 어디서 멈췄는지 클릭 없이 읽힌다.
               선적이 없으면 아래 기존 렌더링이 그대로 돌아간다 — 대다수가 여기다. -->
          <template v-if="cellShips(item, stage)">
            <div
              v-for="sh in visibleShips(item, stage)"
              :key="'sh-' + sh.n"
              class="reqtrack__ship"
              :class="'reqtrack__ship--' + sh.lv"
              @click.stop="openShipDetail(item, sh.n)">
              <span class="reqtrack__sid">{{ shipMark(sh.n) }}</span>
              <span class="reqtrack__sqty">{{ (sh.st === 'none' || sh.st === 'skip') ? '—' : sh.qty }}</span>
              <span class="reqtrack__sdate">
                <template v-if="sh.st === 'done'">✓ {{ sh.date }}</template>
                <template v-else-if="sh.st === 'cur'">● {{ dayText(sh.days + '일째') }}</template>
                <!-- 직납은 화주창고 두 칸을 건너뛴다. ✓ 로 찍으면 거쳐 간 것으로 읽힌다 -->
                <template v-else-if="sh.st === 'skip'">{{ zk('跳过', '건너뜀') }}</template>
                <template v-else>{{ zk('等待', '대기') }}</template>
              </span>
            </div>
            <span
              v-if="hiddenShipCount(item, stage) > 0"
              class="reqtrack__more"
              @click.stop="openShipDetail(item, null)">{{ zk('另有 ' + hiddenShipCount(item, stage) + ' 件 展开', '외 ' + hiddenShipCount(item, stage) + '건 펼치기') }} →</span>
          </template>
          <template v-else-if="cellState(item, stage) === 'done'">
            <span v-if="cellData(item, stage).outcome" class="reqtrack__outcome" :class="{ 'reqtrack__outcome--bad': cellData(item, stage).bad }">{{ stageText(cellData(item, stage).outcome) }}</span>
            <span v-else class="reqtrack__check">✓</span>
            <span class="reqtrack__date">{{ cellData(item, stage).date }}</span>
            <span class="reqtrack__dur">{{ zk('停留 ' + dayZh(cellData(item, stage).dur), cellData(item, stage).dur + ' 머묾') }}</span>
            <span v-if="cellData(item, stage).memo" class="reqtrack__memo-area">
              <i v-if="!memoPreview" class="las la-comment-dots reqtrack__memo-icon--has" :title="cellData(item, stage).memo"></i>
              <span v-else class="reqtrack__memo">{{ cellData(item, stage).memo }}</span>
            </span>
          </template>
          <template v-else-if="cellData(item, stage) && cellData(item, stage).qty !== undefined">
            <!-- 중국재고는 상태가 아니라 '아직 안 나간 잔량' 이다 -->
            <span class="reqtrack__qty">{{ cellData(item, stage).qty.toLocaleString() }}</span>
            <span class="reqtrack__dur">{{ zk('尚未出库的余量', '아직 안 나간 잔량') }}</span>
          </template>
          <template v-else-if="cellState(item, stage) === 'cur'">
            <span class="reqtrack__pulse"></span>
            <span v-if="cellData(item, stage).outcome" class="reqtrack__outcome reqtrack__outcome--cur" :class="`reqtrack__outcome--${cellData(item, stage).lv}`">{{ stageText(cellData(item, stage).outcome) }}</span>
            <span class="reqtrack__badge">{{ dayText(cellData(item, stage).badge) }}</span>
            <span class="reqtrack__date">{{ cellData(item, stage).date }} {{ zk('进入', '진입') }}</span>
            <span v-if="cellData(item, stage).memo" class="reqtrack__memo-area">
              <i v-if="!memoPreview" class="las la-comment-dots reqtrack__memo-icon--has" :title="cellData(item, stage).memo"></i>
              <span v-else class="reqtrack__memo">{{ cellData(item, stage).memo }}</span>
            </span>
          </template>
        </div>
      </template>
    </CDataTable>

    <!-- 범례 -->
    <div class="reqtrack__legend">
      <span class="reqtrack__legend-title">{{ zk('停留提示', '체류 강조') }}</span>
      <span class="reqtrack__legend-item"><i class="sw" style="background:#9aa5b4"></i>{{ zk('正常', '조용') }}</span>
      <span class="reqtrack__legend-item"><i class="sw" style="background:#d99a00"></i>{{ zk('2–3天', '2–3일') }}</span>
      <span class="reqtrack__legend-item"><i class="sw" style="background:#e8730c"></i>{{ zk('4–6天', '4–6일') }}</span>
      <span class="reqtrack__legend-item"><i class="sw" style="background:#d63a3a"></i>{{ zk('7天+', '7일+') }} ⚠</span>
      <span class="reqtrack__legend-item"><i class="sw" style="background:#2faa6a"></i>{{ zk('完成', '완료') }}</span>
      <span class="reqtrack__legend-item"><i class="sw" style="background:#6b48c9"></i>{{ zk('结果栏', '결과 슬롯') }}</span>
    </div>

    <!-- 선적 상세 모달 — 미니줄(①②)을 누르면 여기서 통관 이력까지 본다 -->
    <div v-if="shipDetail" class="reqtrack__note-modal" @click.self="closeShipDetail">
      <div class="reqtrack__ship-box">
        <div class="reqtrack__ship-head">
          <div>
            <div class="reqtrack__ship-title">{{ shipDetail.item.p.name }}</div>
            <div class="reqtrack__ship-meta">{{ shipDetail.item.p.opt }}</div>
          </div>
          <div class="reqtrack__ship-meta">{{ shipDetail.item.p.meta }}</div>
        </div>

        <div class="reqtrack__ship-list">
          <div
            v-for="sh in shipDetail.item.ships"
            :key="'sd-' + sh.n"
            class="reqtrack__ship-card"
            :class="{ 'reqtrack__ship-card--focus': shipDetail.focusN === sh.n }">
            <div class="reqtrack__ship-cardhead">
              <span class="reqtrack__sid">{{ shipMark(sh.n) }}</span>
              <b>{{ sh.수량 }}{{ isCoupass ? '件' : '개' }}</b>
              <span class="reqtrack__ship-badge">{{ stageText(sh.상태 || '출고완료') }}</span>
              <span class="reqtrack__ship-meta">
                <template v-if="sh.blNo">B/L {{ sh.blNo }}</template>
                <template v-if="sh.blNo && sh.입고ID"> · </template>
                <template v-if="sh.입고ID">{{ isCoupass ? '入库ID' : '입고ID' }} {{ sh.입고ID }}</template>
                <template v-if="!sh.blNo && !sh.입고ID">{{ zk('无B/L·入库ID', 'B/L·입고ID 없음') }}</template>
              </span>
            </div>

            <!-- 통관 이력 (Unipass). 없으면 이 블록만 빠진다 -->
            <div v-if="sh.통관 && sh.통관.이력 && sh.통관.이력.length" class="reqtrack__tl">
              <div
                v-for="(h, i) in sh.통관.이력"
                :key="'tl-' + sh.n + '-' + i"
                class="reqtrack__tl-row"
                :class="{ 'reqtrack__tl-row--key': isKeyStep(h), 'reqtrack__tl-row--mute': isBondedMove(h) }">
                <time>{{ fmtUnipass(h.일시원문) }}</time>
                <span>
                  <b>{{ h.단계 }}</b>
                  <small v-if="isBondedMove(h)"> {{ zk('保税运输放行 — 尚未清关，不计入', '보세운송 반출 — 통관 전이라 세지 않음') }}</small>
                  <small v-else-if="h.반출사유"> {{ h.반출사유 }}</small>
                  <small v-if="h.수량"> · {{ h.수량 }}{{ isCoupass ? '件' : '개' }}</small>
                  <small v-if="h.장소"> · {{ h.장소 }}</small>
                </span>
              </div>
            </div>
            <div v-else class="reqtrack__tl-empty">
              {{ zk('暂无清关记录。提交舱单后会自动填入', '통관 이력이 아직 없습니다. 적하목록이 제출되면 자동으로 채워집니다.') }}
            </div>
          </div>
        </div>

        <div v-if="shipDetail.item.dist" class="reqtrack__ship-foot">
          <span v-for="(r, i) in shipDetail.item.dist.줄" :key="'df-' + i">
            {{ stageText(r.상태) }} {{ r.수량.toLocaleString() }}
          </span>
          <span class="reqtrack__ship-total">{{ isCoupass ? '合计' : '합' }} {{ shipDetail.item.dist.합계.toLocaleString() }} / {{ isCoupass ? '求购' : '요청' }} {{ shipDetail.item.dist.구매요청수량.toLocaleString() }}</span>
        </div>

        <div class="reqtrack__note-actions">
          <button type="button" class="reqtrack__note-btn" @click="closeShipDetail">{{ zk('关闭', '닫기') }}</button>
        </div>
      </div>
    </div>

    <!-- 입고사진 — 쿠패스 창고가 입고 때 핸드폰으로 찍은 사진 -->
    <div v-if="photoView" class="reqtrack__note-modal" @click.self="photoView = null">
      <div class="reqtrack__note-box">
        <div class="reqtrack__note-head">{{ zk('入库照片', '입고사진') }} · {{ photoView.p.name }}</div>
        <div class="reqtrack__photos">
          <a v-for="(ph, i) in photoView.photos" :key="ph.url + i" :href="ph.url" target="_blank" rel="noopener" class="reqtrack__photo">
            <img :src="ph.url" alt="입고사진">
            <span>{{ photoLabel(ph.종류) }}<template v-if="ph.이전입고"> · {{ zk('以前入库', '이전 입고') }}</template></span>
          </a>
        </div>
        <div class="reqtrack__note-actions">
          <span class="reqtrack__note-spacer"></span>
          <button type="button" class="reqtrack__note-btn" @click="photoView = null">{{ zk('关闭', '닫기') }}</button>
        </div>
      </div>
    </div>

    <!-- 단계별 메모 편집 모달 -->
    <div v-if="editingNote" class="reqtrack__note-modal" @click.self="closeNote">
      <div class="reqtrack__note-box">
        <div class="reqtrack__note-head">{{ stageText(editingNote.stageLabel) }} {{ zk('备注', '메모') }}</div>
        <textarea
          v-model="editingNote.text"
          class="reqtrack__note-input"
          rows="4"
          :placeholder="zk('请输入此阶段的备注', '이 단계에 대한 메모를 입력하세요')"></textarea>
        <div class="reqtrack__note-actions">
          <button type="button" class="reqtrack__note-btn reqtrack__note-btn--del" :disabled="noteSaving" @click="deleteNote">{{ zk('删除', '삭제') }}</button>
          <button v-if="editingNote.link" type="button" class="reqtrack__note-btn reqtrack__note-btn--link" @click="goNoteLink">{{ linkText(editingNote.linkLabel) }} →</button>
          <span class="reqtrack__note-spacer"></span>
          <button type="button" class="reqtrack__note-btn" :disabled="noteSaving" @click="closeNote">{{ zk('取消', '취소') }}</button>
          <button type="button" class="reqtrack__note-btn reqtrack__note-btn--save" :disabled="noteSaving" @click="saveNote">{{ zk('保存', '저장') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import CDataTable from '@/components/common/CDataTable'
import { STAGES, buildRow, SHIP_INLINE_MAX } from './reqTrackingTransform'
import { PROCESS_STATUS_TEXT } from '@/utils/coupassStatusText'

// 단계 칸·상태 값의 중국어(쿠패스 화면 표시용). 상태값은 PROCESS_STATUS_TEXT, 단계 묶음 이름만 여기
const STAGE_ZH = { 구매확정: '采购确定', 입고현황: '入库状况', 출고현황: '出库状况', 국내창고재고: '韩国仓库库存', 중국창고: '中国仓库' }
function stageZh(ko) {
  return STAGE_ZH[ko] || PROCESS_STATUS_TEXT[ko] || ''
}

// 최근 N일 조회 — 임시 가드(기능 아님).
// 기존 적체(구매전/구매완료 등 미정리 건)를 버티기 위한 한시적 필터.
// 적체 정리 후 제거 예정 → 날짜필터 대신 "종착(쿠팡센터입고완료) 제외, 날짜 무관"으로 전환.
// 종착 이후는 판매현황 SCM(growthScmV0) 소관.
const LOOKBACK_DAYS = 60

export default {
  name: 'ReqPurchaseTracking',
  components: { CDataTable },
  props: {
    // 쿠패스 商品求购 화면 안 탭(进度管理)으로 끼울 때 — 제목을 숨기고 운영자 범위로 본다
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      // 선적 상세 모달 (미니줄 클릭 → 선적 진행 + 통관 이력)
      shipDetail: null,
      stages: STAGES,
      activeFilter: 'all',
      memoPreview: false,
      loading: false,
      rows: [],
      editingNote: null,
      noteSaving: false,
      photoView: null
    }
  },
  mounted() {
    this.loadData()
  },
  computed: {
    // 쿠패스 운영자 화면(logis 라우트)인지 — 셀러 화면과 데이터 범위가 다름
    isCoupass() {
      return this.embedded || this.$route.name === 'logis.reqpurchaseTracking'
    },
    tableHeaders() {
      return [
        { text: this.isCoupass ? '商品信息\n(상품정보)' : '상품정보', value: '상품정보', width: 230, isSlot: true, align: 'start', sortable: false },
        ...STAGES.map(s => {
          const ko = s.label || s.k
          const zh = this.isCoupass && stageZh(ko)
          return { text: zh ? zh + '\n(' + ko + ')' : ko, value: s.k, width: 132, isSlot: true, align: 'center', sortable: false }
        })
      ]
    },
    stageChips() {
      // 현재 머무는 단계별 건수·경고(방치 7일+) 집계
      const byStage = {}
      let totalWarn = 0
      this.rows.forEach(r => {
        const idx = this.currentStageIndex(r)
        if (idx < 0) return
        const sk = STAGES[idx].k
        if (!byStage[sk]) byStage[sk] = { count: 0, warn: 0 }
        byStage[sk].count++
        const c = this.currentCell(r)
        if (c && c.lv === 'red') { byStage[sk].warn++; totalWarn++ }
      })
      // 전체 + 전 단계(0건 포함, STAGES 순서)
      const chips = [{ key: 'all', label: this.zk('全部', '전체'), count: this.rows.length, warn: totalWarn }]
      STAGES.forEach(s => {
        const info = byStage[s.k] || { count: 0, warn: 0 }
        chips.push({ key: s.k, label: this.stageText(s.label || s.k), count: info.count, warn: info.warn })
      })
      return chips
    },
    agingBuckets() {
      const b = { calm: 0, amber: 0, orange: 0, red: 0 }
      this.rows.forEach(r => {
        const c = this.currentCell(r)
        if (c && b[c.lv] !== undefined) b[c.lv]++
      })
      return [
        { key: 'lv-calm', lv: 'calm', label: this.zk('正常(0–1天)', '정상(0–1일)'), count: b.calm },
        { key: 'lv-amber', lv: 'amber', label: this.zk('2–3天', '2–3일'), count: b.amber },
        { key: 'lv-orange', lv: 'orange', label: this.zk('4–6天', '4–6일'), count: b.orange },
        { key: 'lv-red', lv: 'red', label: this.zk('7天+', '7일+'), count: b.red }
      ]
    },
    filteredRows() {
      const f = this.activeFilter
      if (f === 'all') return this.rows
      if (f.indexOf('lv-') === 0) {
        const lv = f.slice(3)
        return this.rows.filter(r => { const c = this.currentCell(r); return c && c.lv === lv })
      }
      return this.rows.filter(r => {
        const idx = this.currentStageIndex(r)
        return idx >= 0 && STAGES[idx].k === f
      })
    }
  },
  methods: {
    // 쿠패스 화면이면 「中文(한국어)」, 셀러 화면이면 한국어 그대로
    zk(zh, ko) {
      return this.isCoupass ? zh + '(' + ko + ')' : ko
    },
    // 단계·상태 값 표시 — 값(한국어)은 그대로 두고 쿠패스 화면에서만 중국어를 붙인다
    stageText(ko) {
      const zh = stageZh(ko)
      return zh ? this.zk(zh, ko) : ko
    },
    // 'N일째' / 'N일' → 第N天 / N天 (모양이 다르면 그대로)
    dayZh(s) {
      const m = String(s || '').match(/^(\d+)일(째)?$/)
      return m ? (m[2] ? '第' + m[1] + '天' : m[1] + '天') : s
    },
    dayText(s) {
      return this.dayZh(s) === s ? s : this.zk(this.dayZh(s), s)
    },
    linkText(ko) {
      const zh = { '재고관리 이동': '前往库存管理', '판매관리(SCM) 이동': '前往销售管理(SCM)', '이동': '前往' }[ko]
      return zh ? this.zk(zh, ko) : ko
    },
    // 카드 클릭: 같은 필터 다시 누르면 해제(전체)
    setFilter(key) {
      this.activeFilter = this.activeFilter === key ? 'all' : key
    },
    // 행의 현재(머무는) 단계 셀 반환
    currentCell(item) {
      for (const s of STAGES) {
        const c = item.cells[s.k]
        if (c && c.st === 'cur') return c
      }
      return null
    },
    currentStageIndex(item) {
      return STAGES.findIndex(s => item.cells[s.k] && item.cells[s.k].st === 'cur')
    },
    cellData(item, stage) {
      return item.cells[stage.k] || {}
    },
    // 셀 상태: done / cur / future / skip
    cellState(item, stage) {
      const c = item.cells[stage.k]
      if (c && c.st === 'done') return 'done'
      if (c && c.st === 'cur') return 'cur'
      const idx = STAGES.indexOf(stage)
      return idx > this.currentStageIndex(item) ? 'future' : 'skip'
    },
    // 셀 래퍼 div 클래스
    cellAttrs(item, stage) {
      const state = this.cellState(item, stage)
      const c = item.cells[stage.k] || {}
      const cls = ['reqtrack__cell', `reqtrack__cell--${state}`]
      if (state === 'done' && c.bad) cls.push('reqtrack__cell--bad')
      if (state === 'cur') cls.push(`reqtrack__cell--${c.lv}`)
      return { class: cls }
    },
    onImgError(e) {
      e.target.style.visibility = 'hidden'
    },
    // 단계 셀 클릭: 거친/현재 단계면 메모 편집 모달 열기
    onCellClick(stage, item) {
      const state = this.cellState(item, stage)
      if (state === 'future' || state === 'skip') return
      this.openNote(item, stage)
    },
    // 메모 편집 모달 열기
    openNote(item, stage) {
      const cur = item.cells[stage.k]
      this.editingNote = {
        item,
        stageKey: stage.k,
        stageLabel: stage.label || stage.k,
        text: (cur && cur.memo) || '',
        link: stage.link || null,
        linkLabel: stage.linkLabel || '이동'
      }
    },
    closeNote() {
      this.editingNote = null
    },
    // 메모 모달 내 연결 메뉴 이동 (예: 국내창고재고 → 재고관리)
    goNoteLink() {
      if (this.editingNote && this.editingNote.link) {
        this.$router.push({ name: this.editingNote.link }).catch(() => {})
      }
    },
    async saveNote() {
      const e = this.editingNote
      if (!e) return
      this.noteSaving = true
      try {
        const res = await axios.post('/api/reqpurchase/updateTrackingNote', {
          _id: e.item._id, stageKey: e.stageKey, text: e.text
        })
        if (res.data && res.data.요청결과 === 'success') {
          this.$set(e.item.cells[e.stageKey], 'memo', e.text.trim() || undefined)
          this.editingNote = null
        }
      } catch (err) {
        console.error('[구매내역관리] 메모 저장 실패', err)
      } finally {
        this.noteSaving = false
      }
    },
    async deleteNote() {
      const e = this.editingNote
      if (!e) return
      this.noteSaving = true
      try {
        const res = await axios.post('/api/reqpurchase/updateTrackingNote', {
          _id: e.item._id, stageKey: e.stageKey, text: ''
        })
        if (res.data && res.data.요청결과 === 'success') {
          this.$set(e.item.cells[e.stageKey], 'memo', undefined)
          this.editingNote = null
        }
      } catch (err) {
        console.error('[구매내역관리] 메모 삭제 실패', err)
      } finally {
        this.noteSaving = false
      }
    },
    // ── 선적 미니줄 (정본 §5 Phase 2-17) ─────────────────────
    // 선적이 없으면 null 을 돌려서 기존 렌더링이 그대로 돌게 한다
    cellShips(item, stage) {
      const c = item.cells && item.cells[stage.k]
      return (c && Array.isArray(c.ships) && c.ships.length) ? c.ships : null
    },
    visibleShips(item, stage) {
      const list = this.cellShips(item, stage)
      return list ? list.slice(0, SHIP_INLINE_MAX) : []
    },
    hiddenShipCount(item, stage) {
      const list = this.cellShips(item, stage)
      return list ? Math.max(0, list.length - SHIP_INLINE_MAX) : 0
    },
    // ①②③… 10건 넘으면 그냥 숫자로
    shipMark(n) {
      const marks = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩']
      return marks[n - 1] || String(n)
    },
    openShipDetail(item, focusN) {
      if (!item.ships || !item.ships.length) return
      this.shipDetail = { item, focusN: focusN || null }
    },
    closeShipDetail() {
      this.shipDetail = null
    },
    photoLabel(kind) {
      const ko = { 입고사진: '상품·포장', 오류사진: '입고오류', 사이즈사진: '사이즈', 확인사진: '확인필요' }[kind]
      const zh = { 입고사진: '商品及包装', 오류사진: '入库错误', 사이즈사진: '尺寸', 확인사진: '需确认' }[kind]
      return ko ? this.zk(zh, ko) : kind
    },
    // '20260806093218' → '08-06 09:32'
    fmtUnipass(t) {
      const v = String(t || '').replace(/\D/g, '')
      if (v.length < 12) return v
      return v.slice(4, 6) + '-' + v.slice(6, 8) + ' ' + v.slice(8, 10) + ':' + v.slice(10, 12)
    },
    // 통관 이력에서 '보세운송 반출' 은 통관 전이라 세지 않는다(정본 §4)
    isBondedMove(h) {
      return String(h.반출사유 || '').indexOf('보세운송') >= 0
    },
    isKeyStep(h) {
      const d = String(h.단계 || '')
      if (d === '수입신고수리') return true
      return d === '반출신고' && String(h.반출사유 || '').indexOf('수리후') >= 0
    },

    // 실데이터 로드: getTrackingList(경량) → buildRow 변환
    async loadData() {
      this.loading = true
      try {
        const body = await this.buildQueryBody()
        if (!body) { this.rows = []; return }
        const res = await axios.post('/api/reqpurchase/getTrackingList', body)
        if (res.data && res.data.요청결과 === 'success') {
          const docs = res.data.reqpurchase || []
          // 선적 정보를 목록 단위로 한 번에 받는다. 건별로 부르면 N+1 이다.
          // 실패해도 화면은 지금 모습 그대로여야 하므로 조용히 빈 값으로 넘어간다.
          let 선적 = {}
          let 분포 = {}
          try {
            const sres = await axios.post('/api/reqpurchase/shipmentDistribution', {
              reqIds: docs.map(d => String(d._id)).slice(0, 1000)
            })
            if (sres.data && sres.data.요청결과 === '성공') {
              선적 = sres.data.선적 || {}
              분포 = sres.data.분포 || {}
            }
          } catch (se) {
            console.error('[구매내역관리] 선적 정보 조회 실패', se)
          }
          this.rows = docs.map(d => {
            const k = String(d._id)
            return buildRow(d, new Date(), { ships: 선적[k] || null, dist: 분포[k] || null })
          })
        } else {
          this.rows = []
        }
      } catch (e) {
        console.error('[구매내역관리] getTrackingList 실패', e)
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    // 셀러: 본인 companyid / 쿠패스: 권한업체(showimport) 전체
    async buildQueryBody() {
      const end = Date.now()
      const start = end - LOOKBACK_DAYS * 24 * 60 * 60 * 1000
      if (this.isCoupass) {
        const r = await axios.post('/api/getcompanyids', {})
        const ids = ((r.data && r.data.result) || [])
          .filter(e => e.auth && e.auth.showimport)
          .map(e => e.companyid)
        if (!ids.length) return null
        return { companyids: ids, start, end }
      }
      const cid = this.$store.state.user.companyid
      if (!cid) return null
      return { companyid: cid, start, end }
    },
    // ── 목업 doc (개발/디자인 검증용, 현재 미사용) ──
    // eslint-disable-next-line no-unused-vars
    buildMockDocs() {
      return [
        { _id: 'm1', 상품명: 'LED 식물 성장등 풀스펙트럼', 옵션1_중국어: '白色-调光调色', 옵션2_중국어: '65W', 구매요청수량: 100, companyid: '보고상점', 등록이미지: '', 진행현황: '협상중', processStatus: '협상중', 요청일: '2026-06-13', processHistory: [
          { toStatus: '구매전', changedAt: '2026-06-13' },
          { toStatus: '협상중', changedAt: '2026-06-14', memo: '판매자 단가 회신 지연. 3차 독촉(06-19)' }
        ] },
        { _id: 'm2', 상품명: '스테인리스 보온 텀블러 500ml', 옵션1_중국어: '银色', 옵션2_중국어: '500ml', 구매요청수량: 120, companyid: '보고상점', 등록이미지: '', processStatus: '배송중', 요청일: '2026-06-13', processHistory: [
          { toStatus: '구매전', changedAt: '2026-06-13' }, { toStatus: '협상중', changedAt: '2026-06-14' },
          { toStatus: '구매확인', changedAt: '2026-06-16' }, { toStatus: '입금요청', changedAt: '2026-06-16' }, { toStatus: '입금완료', changedAt: '2026-06-16' },
          { toStatus: '구매완료', changedAt: '2026-06-17', memo: '1688주문 25081xxx' }, { toStatus: '배송중', changedAt: '2026-06-18', memo: '광저우 출발, SF13xx' }
        ] },
        { _id: 'm3', 상품명: '무드등 미니 빔프로젝터', 옵션1_중국어: '白色', 옵션2_중국어: '1080p', 구매요청수량: 30, companyid: '보고상점', 등록이미지: '', processStatus: '구매취소', 요청일: '2026-06-12', processHistory: [
          { toStatus: '구매전', changedAt: '2026-06-12' }, { toStatus: '협상중', changedAt: '2026-06-13' },
          { toStatus: '구매취소', changedAt: '2026-06-15', memo: '판매자 단가 30%↑ 통보 → 취소' }
        ] },
        { _id: 'm4', 상품명: '유리 밀폐용기 4종 세트', 옵션1_중국어: '透明', 옵션2_중국어: '4P', 구매요청수량: 80, companyid: '보고상점', 등록이미지: '', processStatus: '입고작업', 요청일: '2026-06-12', processHistory: [
          { toStatus: '구매전', changedAt: '2026-06-12' }, { toStatus: '협상중', changedAt: '2026-06-13' }, { toStatus: '구매확인', changedAt: '2026-06-14' },
          { toStatus: '입금요청', changedAt: '2026-06-14' }, { toStatus: '입금완료', changedAt: '2026-06-15' }, { toStatus: '구매완료', changedAt: '2026-06-15' },
          { toStatus: '배송중', changedAt: '2026-06-16' }, { toStatus: '배송완료', changedAt: '2026-06-18' },
          { toStatus: '입고작업', changeDate: new Date('2026-06-18').getTime(), memo: '중국창고 검수·분류 진행중' }
        ] },
        { _id: 'm5', 상품명: '방수 여행용 파우치 세트', 옵션1_중국어: '灰色', 옵션2_중국어: 'L', 구매요청수량: 50, companyid: '미니멀리스트', 등록이미지: '', processStatus: '입고오류', 요청일: '2026-06-10', processHistory: [
          { toStatus: '구매전', changedAt: '2026-06-10' }, { toStatus: '협상중', changedAt: '2026-06-11' }, { toStatus: '구매확인', changedAt: '2026-06-12' },
          { toStatus: '입금요청', changedAt: '2026-06-12' }, { toStatus: '입금완료', changedAt: '2026-06-13' }, { toStatus: '구매완료', changedAt: '2026-06-13' },
          { toStatus: '배송중', changedAt: '2026-06-14' }, { toStatus: '배송완료', changedAt: '2026-06-15' },
          { toStatus: '입고작업', changeDate: new Date('2026-06-15').getTime() },
          { toStatus: '입고오류', changeDate: new Date('2026-06-16').getTime(), memo: '수량불일치 50→47 · 반품확인 진행중' }
        ] },
        { _id: 'm6', 상품명: '실리콘 폴더블 물병 600ml', 옵션1_중국어: '蓝色', 옵션2_중국어: '600ml', 구매요청수량: 40, companyid: '미니멀리스트', 등록이미지: '', processStatus: '중국재고', 요청일: '2026-06-04', processHistory: [
          { toStatus: '구매전', changedAt: '2026-06-04' }, { toStatus: '협상중', changedAt: '2026-06-05' }, { toStatus: '구매확인', changedAt: '2026-06-06' },
          { toStatus: '입금요청', changedAt: '2026-06-06' }, { toStatus: '입금완료', changedAt: '2026-06-07' }, { toStatus: '구매완료', changedAt: '2026-06-08' },
          { toStatus: '배송중', changedAt: '2026-06-09' }, { toStatus: '배송완료', changedAt: '2026-06-09' },
          { toStatus: '입고작업', changeDate: new Date('2026-06-09').getTime() }, { toStatus: '입고완료', changeDate: new Date('2026-06-09').getTime() },
          { toStatus: '중국재고', changedAt: '2026-06-09', memo: '합포장 대기(정상). 다음 LCL 06-25' }
        ] },
        { _id: 'm7', 상품명: '무선 고속 충전 패드 15W', 옵션1_중국어: '黑色', 옵션2_중국어: '15W', 구매요청수량: 60, companyid: '보고상점', 등록이미지: '', processStatus: '쿠팡센터입고완료', 요청일: '2026-06-01', processHistory: [
          { toStatus: '구매전', changedAt: '2026-06-01' }, { toStatus: '협상중', changedAt: '2026-06-02' }, { toStatus: '구매확인', changedAt: '2026-06-03' },
          { toStatus: '입금요청', changedAt: '2026-06-03' }, { toStatus: '입금완료', changedAt: '2026-06-04' }, { toStatus: '구매완료', changedAt: '2026-06-05' },
          { toStatus: '배송중', changedAt: '2026-06-08' }, { toStatus: '배송완료', changedAt: '2026-06-09' },
          { toStatus: '입고작업', changeDate: new Date('2026-06-09').getTime() }, { toStatus: '입고완료', changeDate: new Date('2026-06-10').getTime() }, { toStatus: '중국재고', changedAt: '2026-06-10' },
          { toStatus: '출고요청', changedAt: '2026-06-12' }, { toStatus: '출고완료', changedAt: '2026-06-12' },
          { toStatus: '해외입고중', changedAt: '2026-06-12' }, { toStatus: '국내입고중', changedAt: '2026-06-14' }, { toStatus: '국내창고입고중', changedAt: '2026-06-15' }, { toStatus: '국내창고입고완료', changedAt: '2026-06-16' },
          { toStatus: '쿠팡센터입고완료', changedAt: '2026-06-17', memo: '쿠팡센터 입고확인 대기' }
        ] }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
$done: #2faa6a; $done-bg: #eaf7f0;
$calm: #9aa5b4; $calm-bg: #f4f6f8;
$amber: #d99a00; $amber-bg: #fff6da;
$orange: #e8730c; $orange-bg: #ffe9d6;
$red: #d63a3a; $red-bg: #ffe0e0;

.reqtrack {
  padding: 16px 20px;

  &__head { margin-bottom: 14px; }
  &__title { font-size: 18px; font-weight: 700; margin: 0 0 2px; }
  &__sub { color: #7a8699; font-size: 12.5px; margin: 0; }

  &__state { padding: 48px 0; text-align: center; color: #7a8699; font-size: 13px; }
  // 현황 대시보드 (단계별 / 체류기간별 2행)
  &__dash { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
  &__dash-row { display: flex; gap: 6px; flex-wrap: wrap; align-items: stretch; }
  &__dash-title {
    align-self: center; font-size: 11px; font-weight: 700; color: #7a8699;
    width: 56px; flex: none;
  }
  &__card {
    background: #fff; border: 1px solid #e3e6ea; border-radius: 9px;
    padding: 7px 11px; min-width: 72px; cursor: pointer; text-align: left;
    display: flex; flex-direction: column; gap: 2px; transition: .12s;
    &:hover { border-color: #b9c4d4; }
    &--all { background: #f7f9fc; }
    &--empty { opacity: .42; }
    &--active { border-color: #2f6fed; box-shadow: inset 0 0 0 1px #2f6fed; opacity: 1; }
    &--warn { border-color: #f0c3c3; }
    &--warn.reqtrack__card--active { border-color: $red; box-shadow: inset 0 0 0 1px $red; }
    // 체류기간 카드 색
    &--aging.reqtrack__card--calm { .reqtrack__card-count { color: $calm; } }
    &--aging.reqtrack__card--amber { .reqtrack__card-count { color: $amber; } }
    &--aging.reqtrack__card--orange { .reqtrack__card-count { color: $orange; } }
    &--aging.reqtrack__card--red { border-color: #f0c3c3; .reqtrack__card-count { color: $red; } }
  }
  &__card-label { font-size: 11px; color: #7a8699; white-space: nowrap; }
  &__card-row { display: flex; align-items: baseline; gap: 5px; }
  &__card-count { font-size: 18px; font-weight: 700; line-height: 1.1; color: #1f2733; }
  &__card-warn { font-size: 10.5px; font-weight: 700; color: $red; white-space: nowrap; }

  // 메모 미리보기 토글 (테이블 우측 상단)
  &__toolbar { display: flex; justify-content: flex-end; margin-bottom: 8px; }
  &__switch {
    display: inline-flex; align-items: center; gap: 8px; cursor: pointer; user-select: none;
    font-size: 12.5px; color: #56627a;
    input { display: none; }
    input:checked + .reqtrack__switch-track { background: #2f6fed; }
    input:checked + .reqtrack__switch-track .reqtrack__switch-thumb { transform: translateX(16px); }
  }
  &__switch-track {
    position: relative; width: 36px; height: 20px; background: #cdd4dd; border-radius: 10px;
    transition: .15s; flex: none;
  }
  &__switch-thumb {
    position: absolute; top: 2px; left: 2px; width: 16px; height: 16px;
    background: #fff; border-radius: 50%; transition: .15s;
  }
  &__switch-label { font-weight: 600; }

  // 셀 메모: 미리보기 ON이면 텍스트, OFF면 메모 있음 표시 아이콘 (편집은 셀 클릭)
  &__memo-area { display: flex; align-items: flex-start; gap: 4px; margin-top: 4px; width: 100%; }
  &__memo-icon--has { font-size: 13px; color: #2f6fed; flex: none; line-height: 1.2; }

  // 메모 편집 모달
  &__note-modal {
    position: fixed; inset: 0; background: rgba(0, 0, 0, .35); z-index: 1000;
    display: flex; align-items: center; justify-content: center;
  }
  &__note-box {
    background: #fff; border-radius: 12px; width: 380px; max-width: 92vw; padding: 18px 18px 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, .25);
  }
  &__note-head { font-size: 14px; font-weight: 700; margin-bottom: 10px; color: #1f2733; }
  &__note-input {
    width: 100%; border: 1px solid #d7dbe0; border-radius: 8px; padding: 9px 11px;
    font-size: 13px; line-height: 1.5; resize: vertical; font-family: inherit;
    &:focus { outline: none; border-color: #2f6fed; }
  }
  &__note-actions { display: flex; align-items: center; gap: 8px; margin-top: 12px; }
  &__note-spacer { flex: 1; }
  &__note-btn {
    border: 1px solid #d7dbe0; background: #fff; border-radius: 7px; padding: 6px 14px;
    font-size: 12.5px; cursor: pointer; color: #56627a;
    &:hover { background: #f4f6f8; }
    &:disabled { opacity: .5; cursor: default; }
    &--save { background: #2f6fed; border-color: #2f6fed; color: #fff; &:hover { background: #2960d8; } }
    &--del { border-color: #f0c3c3; color: $red; &:hover { background: $red-bg; } }
    &--link { border-color: #b9c9ef; color: #2f6fed; &:hover { background: #eef3ff; } }
  }

  // 상품 셀
  &__prod { display: flex; gap: 9px; align-items: flex-start; padding: 8px 4px; white-space: normal; }
  &__thumb { width: 36px; height: 36px; border-radius: 6px; object-fit: cover; flex: none; background: #e6eaef; }
  &__thumb--empty { display: inline-block; }
  &__pname { font-weight: 600; font-size: 12.5px; line-height: 1.3; }
  &__popt { color: #7a8699; font-size: 11px; margin-top: 2px; }
  &__pmeta { color: #7a8699; font-size: 11px; margin-top: 2px; }
  &__photo-link { display: inline-block; margin-top: 2px; font-size: 11px; color: #2f6fd6; }
  &__photos { display: flex; flex-wrap: wrap; gap: 8px; max-height: 60vh; overflow-y: auto; margin-bottom: 10px; }
  &__photo { display: block; width: 120px; text-align: center; font-size: 11px; color: #7a8699; }
  &__photo img { width: 120px; height: 120px; object-fit: cover; border: 1px solid #ddd; border-radius: 4px; display: block; }

  // 단계 셀
  &__cellwrap { min-height: 56px; }
  &__cellwrap--clickable { cursor: pointer; &:hover { filter: brightness(.975); } }
  &__cell {
    padding: 7px 9px; min-height: 56px; display: flex; flex-direction: column; gap: 2px;
    position: relative; white-space: normal; text-align: left; border-radius: 3px;
    &--done { background: $done-bg; .reqtrack__date { color: #1f7d4d; } .reqtrack__check { color: $done; } }
    &--bad { background: $red-bg; .reqtrack__date { color: $red; } }
    &--future { background: repeating-linear-gradient(45deg, #fcfcfd, #fcfcfd 5px, #f5f6f8 5px, #f5f6f8 10px); }
    &--skip { background: #fbfbfc; }
    // current 강조
    &--calm { background: $calm-bg; box-shadow: inset 0 0 0 2px $calm; color: $calm; }
    &--amber { background: $amber-bg; box-shadow: inset 0 0 0 2px $amber; color: #9c6f00; }
    &--orange { background: $orange-bg; box-shadow: inset 0 0 0 2px $orange; color: $orange; }
    &--red { background: $red-bg; box-shadow: inset 0 0 0 2px $red; color: $red; }
  }
  &__check { position: absolute; top: 6px; right: 8px; font-size: 10px; }

  // ── 선적 미니줄 ─────────────────────────────────────
  // 칸 안에서 선적 하나가 한 줄. ①②가 칸을 가로질러 정렬돼야 읽힌다
  &__ship {
    display: flex; align-items: center; gap: 5px; padding: 2px 0; cursor: pointer;
    &:hover { filter: brightness(.94); }
    & + & { border-top: 1px dashed #d7dbe0; }
    &--done { color: #1f7d4d; }
    &--calm { color: $calm; }
    &--amber { color: #9c6f00; }
    &--orange { color: $orange; }
    &--red { color: $red; }
    &--none { color: #9aa5b4; }
  }
  &__sid {
    flex: none; width: 15px; height: 15px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 9px; font-weight: 700; border: 1px solid currentColor; background: #fff;
  }
  &__sqty { font-size: 11px; font-weight: 600; min-width: 24px; font-variant-numeric: tabular-nums; }
  &__sdate { font-size: 10.5px; color: #56627a; font-variant-numeric: tabular-nums; }
  &__more { font-size: 10px; color: #2f6fed; padding-top: 3px; cursor: pointer; &:hover { text-decoration: underline; } }
  &__qty { font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; }

  // ── 선적 상세 모달 ──────────────────────────────────
  &__ship-box {
    background: #fff; border-radius: 12px; width: 520px; max-width: 94vw;
    max-height: 82vh; overflow-y: auto; padding: 16px 18px 12px;
    display: flex; flex-direction: column; gap: 12px;
  }
  &__ship-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
  &__ship-title { font-size: 14px; font-weight: 700; color: #1f2733; }
  &__ship-meta { font-size: 11px; color: #7a8699; }
  &__ship-list { display: flex; flex-direction: column; gap: 10px; }
  &__ship-card {
    border: 1px solid #e3e6ea; border-radius: 8px; padding: 9px 11px;
    display: flex; flex-direction: column; gap: 7px;
    &--focus { border-color: #2f6fed; box-shadow: inset 0 0 0 1px #2f6fed; }
  }
  &__ship-cardhead { display: flex; align-items: center; gap: 7px; font-size: 12px; flex-wrap: wrap; }
  &__ship-badge {
    font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 9px;
    background: $done-bg; color: #1f7d4d; border: 1px solid #b7e2c9;
  }
  &__tl { display: flex; flex-direction: column; }
  &__tl-row {
    display: grid; grid-template-columns: 84px 1fr; gap: 9px;
    font-size: 11.5px; padding: 3px 0 3px 10px;
    border-left: 2px solid #e3e6ea; margin-left: 3px;
    time { color: #7a8699; font-variant-numeric: tabular-nums; }
    b { font-weight: 600; color: #1f2733; }
    small { color: #7a8699; }
    &--key { border-left-color: $done; }
    &--mute { opacity: .55; }
  }
  &__tl-empty { font-size: 11.5px; color: #7a8699; padding-left: 13px; }
  &__ship-foot {
    display: flex; flex-wrap: wrap; gap: 10px;
    border-top: 1px solid #e3e6ea; padding-top: 9px;
    font-size: 11.5px; color: #56627a; font-variant-numeric: tabular-nums;
  }
  &__ship-total { margin-left: auto; font-weight: 700; color: #1f2733; }
  &__date { display: block; width: 100%; font-weight: 600; font-size: 11.5px; color: #222; }
  &__dur { display: block; width: 100%; font-size: 10px; color: #7a8699; margin-top: 1px; }
  &__memo {
    display: block; width: 100%; font-size: 10px; color: #56627a; line-height: 1.35;
    border-top: 1px dashed #d7dbe0; padding-top: 4px; margin-top: 5px;
    white-space: pre-line; word-break: break-word;
  }
  &__badge {
    display: inline-flex; align-items: center; gap: 3px; font-weight: 700; font-size: 10.5px;
    align-self: flex-start; padding: 2px 7px; border-radius: 10px; background: #fff; border: 1px solid currentColor;
  }
  &__pulse { position: absolute; top: 7px; right: 8px; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  &__outcome {
    font-weight: 700; font-size: 11px; align-self: flex-start; padding: 1px 7px; border-radius: 9px;
    background: $done-bg; color: #1f7d4d; border: 1px solid #b7e2c9;
    &--bad { background: #fff; color: $red; border-color: #f1b6b6; }
    &--cur { background: #fff; }
    &--red { color: $red; border-color: #f1b6b6; }
    &--orange { color: $orange; border-color: #f3cba3; }
    &--amber { color: #9c6f00; border-color: #f0d79a; }
    &--calm { color: #6a7585; }
  }

  &__legend {
    display: flex; gap: 14px; flex-wrap: wrap; margin-top: 12px;
    color: #7a8699; font-size: 11.5px; align-items: center;
    .sw { width: 11px; height: 11px; border-radius: 3px; display: inline-block; margin-right: 5px; vertical-align: middle; }
  }
  &__legend-title { font-weight: 600; color: #1f2733; }
  &__legend-item { display: inline-flex; align-items: center; }
}
</style>
